// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type QueryReturnValue,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { engineApi } from '@shared/data-access/api-base/citec-engine-api';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';
import {
  type WatchlistResp,
  type RemoveWatchlistItem,
} from '@shared/data-access/interfaces/api/engine/citec';
import { buildWatchlistPositions } from '@shared/data-access/transformers/assets/build-watchlist-positions';
import { selectAccountCurrency } from '@shared/data-access/slices/user/user-selectors';
import { SharedTagTypes } from '@shared/data-access/utils/constants';

export const watchlistApi = engineApi.injectEndpoints({
  endpoints: build => ({
    getWatchlist: build.query<WatchListPosition[], string | void>({
      queryFn: async (_, _queryApi, _extraOptions, fetchWithBQ) => {
        const response = (await fetchWithBQ({
          url: '/citec/watchlist/',
          method: 'GET',
        })) as QueryReturnValue<
          WatchlistResp[],
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error) return { error: response.error as FetchBaseQueryError };

        const state = _queryApi.getState();

        const currency = selectAccountCurrency(state as any);

        const result = await buildWatchlistPositions(
          response.data,
          _queryApi.dispatch,
          currency,
        );

        return {
          data: result,
        };
      },
      extraOptions: {
        includeCurrency: true,
        useTrackPromise: true,
      },
      providesTags: [
        { type: 'Watchlist', id: 'WatchlistPositions' },
        SharedTagTypes.CurrencyConversion,
      ],
    }),

    addToWatchlist: build.mutation<WatchlistResp[], WatchlistResp[]>({
      query: payload => ({
        url: '/citec/watchlist/',
        method: 'POST',
        body: payload,
      }),
    }),

    deleteFromWatchlist: build.mutation<any, RemoveWatchlistItem>({
      query: params => ({
        url: '/citec/watchlist/',
        method: 'DELETE',
        params,
      }),
    }),
  }),
});
